// The AI packaging prompt, exported as a constant so the builder UI can offer
// a "Copy prompt for your AI" button (plan §9). This file is the prompt's
// source of truth. Its multi-format manifest shape is the agreed final target;
// the current importer implements the single-image subset while the feature is
// behind its launch flag.

export const CREATIVE_IMPORT_PACKAGING_PROMPT = `We're done creating. Now package the approved ad creatives from this chat for import into my ad tool. Do not create or modify any creative content in this step.

**What to include:** preserve the assets, formats, groupings, order, and final copy I approved or clearly selected. Leave out rejected versions and drafts unless I say otherwise. Only use carousel or dynamic when I explicitly established that format; otherwise keep assets as separate creatives. If anything is genuinely unclear, ask me before packaging.

**Build this structure and zip it:**
\`\`\`
creative_import/
  creatives.json
  media/
    <the image and video files>
\`\`\`

**creatives.json format** — UTF-8 JSON using this shape:
\`\`\`json
{
  "manifest_version": 1,
  "creatives": [
    {
      "creative_name": "Fall Roast Launch",
      "format": "single_image",
      "media_path": "media/fall-roast.jpg",
      "copy": {
        "primary_text": "Final primary text from this chat",
        "headline": "Final headline from this chat",
        "description": "Final description from this chat",
        "call_to_action": "SHOP_NOW",
        "destination_url": "https://example.com/fall-roast",
        "url_parameters": "utm_source=facebook&utm_campaign=fall_roast",
        "graphic_text": "Final on-image text from this chat"
      }
    },
    {
      "creative_name": "Fall Product Carousel",
      "format": "carousel",
      "copy": {
        "primary_text": "Final shared carousel text from this chat",
        "call_to_action": "SHOP_NOW",
        "destination_url": "https://example.com/fall"
      },
      "slides": [
        {
          "media_path": "media/fall-roast.jpg",
          "copy": {
            "headline": "Fall Roast",
            "description": "Small-batch seasonal roast"
          }
        },
        {
          "media_path": "media/fall-mug.jpg",
          "copy": {
            "headline": "Limited Edition Mug",
            "destination_url": "https://example.com/fall-mug"
          }
        }
      ]
    },
    {
      "creative_name": "Fall Collection Dynamic",
      "format": "dynamic",
      "media_paths": [
        "media/fall-roast.jpg",
        "media/fall-mug.jpg",
        "media/fall-collection.gif"
      ],
      "copy": {
        "primary_texts": ["First final primary text", "Second final primary text"],
        "headlines": ["First final headline", "Second final headline"],
        "descriptions": ["First final description", "Second final description"],
        "call_to_actions": ["SHOP_NOW", "LEARN_MORE"],
        "destination_url": "https://example.com/fall"
      }
    }
  ]
}
\`\`\`
Rules:
- \`format\` is required and must be \`single_image\`, \`single_video\`, \`carousel\`, or \`dynamic\`. A single video uses the same shape as a single image with \`format\`: \`single_video\`. Treat .gif as video.
- Use \`media_path\` for a single creative or carousel slide, and \`media_paths\` for a dynamic creative. Paths are relative and must exactly match file paths and casing inside the zip. Supported files: .jpg/.jpeg/.png images and .mp4/.mov/.gif videos.
- Carousel \`slides\` are ordered and contain 2–5 items. Dynamic media and copy arrays are independent pools that may be freely recombined.
- Give each creative a short, human-findable \`creative_name\`, preferring an established name from this chat.
- All \`copy\` fields are optional. Include final copy already approved or clearly selected in this chat, verbatim and attached at the correct creative or carousel-slide level. Never draft, rewrite, improve, or fill in missing copy during packaging; omit absent fields or the whole \`copy\` object.

Give me the final \`.zip\` to download.`;
